"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BrainCircuit, Feather } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + gestochen scharfer Gewichts-Vergleich (15 MB vs. 480 MB)
 * über vertikale Y-Physik statt unscharfem Text-Scale/Tilt.
 */
export default function Preview() {
  const [dropped, setDropped] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setDropped((v) => !v), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{
          scale: dropped ? [1, 1.3, 1.1] : 0.85,
          opacity: dropped ? 0.85 : 0.4,
        }}
        transition={{ duration: 0.8 }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-600/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex items-end gap-5 antialiased">
        {/* Links: Federleichte App (15 MB) */}
        <motion.div
          animate={{ y: dropped ? -14 : 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 18 }}
          className="flex flex-col items-center gap-1 rounded-xl border border-emerald-400/70 bg-white px-3 py-2 shadow-lg dark:bg-zinc-900"
        >
          <Feather className="h-4 w-4 text-emerald-500" />
          <span className="font-mono text-[11px] font-black text-emerald-600 dark:text-emerald-400">
            15 MB
          </span>
          <span className="text-[9px] font-bold text-neutral-500 dark:text-neutral-400">
            Web-App
          </span>
        </motion.div>

        {/* Rechts: Schwerer KI-Monolith (480 MB) */}
        <motion.div
          animate={{ y: dropped ? 8 : -18 }}
          transition={{ type: "spring", stiffness: 320, damping: 16 }}
          className="relative flex flex-col items-center gap-1 rounded-2xl border border-purple-400/80 bg-gradient-to-b from-purple-600 to-indigo-800 px-4 py-3 text-white shadow-[0_20px_40px_-6px_rgba(147,51,234,0.75)]"
        >
          <BrainCircuit className="h-5 w-5 text-purple-200" />
          <span className="font-mono text-base font-black tracking-tight">
            480 MB
          </span>
          <span className="rounded bg-white/20 px-1.5 py-0.5 font-sans text-[9px] font-extrabold uppercase tracking-wider">
            KI-Modell
          </span>
        </motion.div>
      </div>
    </div>
  );
}
